import {
  boundsFromCenterSize,
  componentLocalSize,
  orientedSize,
  overflowDistance,
  transformPointByClass
} from './geometry.js';

// PSU orientation is now a stable axis contract rather than a pool of arbitrary box
// permutations. Ordinary layouts use the same orientation seen in the S5M: the PSU's
// narrow 63.5/86 mm axis crosses chassis X, its 100/140 mm body axis is on Y, and its
// long 125/150 mm axis runs front-to-back on Z. Front/rear facing variants only change
// signs, never which physical dimension owns an axis.
export const PSU_CANONICAL_ORIENTATIONS = Object.freeze([17,19,21,23]);
// Deliberate 90-degree yaw used by front-hung SFX brackets: keep the PSU upright on Y,
// but put its shallow 63.5/86 mm axis front-to-back. This is the only ordinary alternate
// axis permutation; it is not part of the generic orientation pool.
export const PSU_FRONT_FACING_ORIENTATIONS = Object.freeze([16,18,20,22]);
// Vertical sandwich rotates only the long PSU axis from Z onto chassis Y while preserving
// the same narrow X face. Again, the variants differ only by facing/sign.
export const PSU_VERTICAL_ORIENTATIONS = Object.freeze([1,3,9,11]);

// Backwards-compatible aliases used by the solver/configuration layer. Keeping these names
// prevents unrelated code from learning a new vocabulary while removing the old 0/4/16/17
// and eight-way upright permutations that caused unstable visual rotations.
export const PSU_BASE_FLAT_ORIENTATIONS = PSU_CANONICAL_ORIENTATIONS;
export const PSU_SIDE_FLAT_ORIENTATIONS = PSU_CANONICAL_ORIENTATIONS;
export const PSU_HORIZONTAL_ORIENTATIONS = PSU_CANONICAL_ORIENTATIONS;
export const PSU_NARROW_X_ORIENTATIONS = PSU_CANONICAL_ORIENTATIONS;
export const PSU_HORIZONTAL_FLIPPED_ORIENTATIONS = PSU_CANONICAL_ORIENTATIONS;
export const PSU_UPRIGHT_ORIENTATIONS = PSU_VERTICAL_ORIENTATIONS;

export const STANDARD_FAN_THICKNESS = 25;
export const SLIM_FAN_THICKNESS = 15;

function finite(value, fallback=0){
  const n=Number(value);
  return Number.isFinite(n)?n:fallback;
}

export function radiatorRepresentative(size) {
  const n = finite(size,0);
  if (n >= 420) return {l:455,w:140,h:30};
  if (n >= 360) return {l:397,w:120,h:30};
  if (n >= 280) return {l:317,w:140,h:30};
  if (n >= 240) return {l:277,w:120,h:30};
  if (n >= 140) return {l:160,w:140,h:30};
  return {l:157,w:120,h:30};
}

export function radiatorFanFrameSize(size) {
  const n=finite(size,0);
  return (n===140 || n===280 || n>=420) ? 140 : 120;
}

export function radiatorFanCount(size) {
  const n=finite(size,0), frame=radiatorFanFrameSize(n);
  return Math.max(1,Math.round(n/frame));
}

export function structuralCapabilityEnvelope(effective={}) {
  const limits=effective?.limits||{};
  const radSize=finite(limits.maxAIO,0);
  return {
    gpu:{
      l:finite(limits.maxGPU,0),
      h:finite(limits.maxGPUH,0),
      t:finite(limits.maxGPUT,0)
    },
    cpu:{h:finite(limits.maxCPU,0)},
    radiator:radSize>0?{size:radSize,dims:radiatorRepresentative(radSize)}:null,
    psu:{horizontalOnly:String(effective?.layoutFamily||'reference')!=='console'},
    modeTraits:{...(effective?.modeTraits||{})}
  };
}

function normalizedFanThickness(value, profile='standard') {
  const explicit=finite(value,0);
  if(explicit>0)return explicit;
  return String(profile||'').toLowerCase().includes('slim')?SLIM_FAN_THICKNESS:STANDARD_FAN_THICKNESS;
}

export function coolingAssemblyProfile(effective={}, radiatorDesc=null) {
  const size=finite(radiatorDesc?.radiatorSize,0) || finite(effective?.configurationBranch?.rad,0) || finite(effective?.limits?.maxAIO,0);
  const frame=finite(radiatorDesc?.fanSize,0) || radiatorFanFrameSize(size);
  const count=finite(radiatorDesc?.fanCount,0) || radiatorFanCount(size);
  const branchProfile=effective?.configurationBranch?.radFan || radiatorDesc?.radFan || radiatorDesc?.fanProfile || effective?.spatialHints?.radFanProfile || 'standard';
  const defaultThickness=normalizedFanThickness(
    effective?.configurationBranch?.fanThickness ?? radiatorDesc?.fanThickness ?? effective?.spatialHints?.radFanThickness,
    branchProfile
  );
  // Exact live per-slot fan geometry outranks the AIO's bundled-fan profile. This lets a
  // 15 mm replacement actually reduce the physical stack seen by the next relational solve.
  const descriptorSegments=Array.isArray(radiatorDesc?.fanSegments) && radiatorDesc.fanSegments.length
    ? radiatorDesc.fanSegments
    : (Array.isArray(effective?.configurationBranch?.fanSegments) && effective.configurationBranch.fanSegments.length
      ? effective.configurationBranch.fanSegments
      : null);
  const explicitSegments=descriptorSegments || (Array.isArray(effective?.spatialHints?.radFanSegments)
    ? effective.spatialHints.radFanSegments
    : null);
  const effectiveCount=explicitSegments?.length || count;
  const segments=[];
  for(let index=0;index<effectiveCount;index+=1){
    const raw=explicitSegments?.[index];
    const segmentFrame=finite(typeof raw==='object'?raw?.frame:0,0) || frame;
    const thickness=normalizedFanThickness(
      typeof raw==='object'?raw?.thickness:raw,
      typeof raw==='object'?raw?.profile:branchProfile
    );
    const z=(index-(effectiveCount-1)/2)*segmentFrame;
    segments.push({index,frame:segmentFrame,thickness,z,manual:!!(typeof raw==='object'&&raw?.manual)});
  }
  return {
    radiatorSize:size,
    fanProfile:branchProfile,
    fanCount:effectiveCount,
    fanFrame:frame,
    fanThicknessMax:Math.max(...segments.map(s=>s.thickness),0),
    segments
  };
}

function boxForDescriptor(desc,placement){
  const local=componentLocalSize(desc.type,desc.dims);
  const size=orientedSize(local,placement.orientationClass);
  return boundsFromCenterSize(placement.position,size);
}

function fanReservation(desc, radiatorPlacement, segment, sign, index) {
  const radThickness=finite(desc?.dims?.h,30);
  const localOffset=[0,sign*(radThickness/2+segment.thickness/2),segment.z];
  const rotated=transformPointByClass(localOffset,radiatorPlacement.orientationClass);
  return {
    desc:{
      type:'fan_reservation',
      actual:false,
      structuralReservation:true,
      dims:{l:segment.frame,w:segment.frame,h:segment.thickness},
      fanIndex:index
    },
    placement:{
      type:'fan_reservation',
      id:`${radiatorPlacement.id}_fan_${index}_${sign>0?'p':'n'}`,
      position:[
        radiatorPlacement.position[0]+rotated[0],
        radiatorPlacement.position[1]+rotated[1],
        radiatorPlacement.position[2]+rotated[2]
      ],
      orientationClass:radiatorPlacement.orientationClass,
      mount:`${radiatorPlacement.mount}_fan_stack`,
      priority:radiatorPlacement.priority||0,
      structuralReservation:true
    }
  };
}

export function coolingAssemblyReservations(bounds, radiatorDesc, radiatorPlacement, effective={}) {
  if(!radiatorDesc||!radiatorPlacement)return[];
  const profile=coolingAssemblyProfile(effective,radiatorDesc);
  const choices=[-1,1].map(sign=>{
    const fans=profile.segments.map((segment,index)=>fanReservation(radiatorDesc,radiatorPlacement,segment,sign,index));
    const overflow=fans.reduce((sum,item)=>sum+overflowDistance(bounds,boxForDescriptor(item.desc,item.placement)),0);
    return {sign,fans,overflow};
  }).sort((a,b)=>a.overflow-b.overflow);
  const chosen=choices[0];
  // Keep the assembly serializable. Callers may attach the returned assembly to the
  // radiator placement itself, so retaining the exact same placement object here would
  // create radiatorPlacement -> coolingAssembly -> radiatorPlacement cycles.
  const radiatorPlacementSnapshot={...radiatorPlacement};
  delete radiatorPlacementSnapshot.coolingAssembly;
  return [
    {desc:radiatorDesc,placement:radiatorPlacementSnapshot,kind:'radiator'},
    ...chosen.fans.map(item=>({...item,kind:'fan'}))
  ];
}

export function coolingAssemblyDepth(effective={}, radiatorDesc=null) {
  const dims=radiatorDesc?.dims || radiatorRepresentative(effective?.configurationBranch?.rad || effective?.limits?.maxAIO || 0);
  const profile=coolingAssemblyProfile(effective,radiatorDesc || {radiatorSize:effective?.configurationBranch?.rad || effective?.limits?.maxAIO || 0,dims});
  return finite(dims?.h,30)+profile.fanThicknessMax;
}

export function catalogRadiatorMountPreferences(effective={}, radiatorSize=0, radiatorSpec={}) {
  const hints=Array.isArray(effective?.catalogSpatialHints?.radiators)
    ? effective.catalogSpatialHints.radiators
    : [];
  const selectedSize=finite(radiatorSize,0);
  const radiatorThickness=finite(radiatorSpec?.radiatorThickness,0);
  const stackThickness=finite(radiatorSpec?.stackThickness,0);
  const out=[];
  const push=value=>{if(value&&!out.includes(value))out.push(value);};
  for(const hint of hints){
    // Conditions such as "with GPU waterblock" are preserved by the compiler, but are not
    // assumed true here. Conditional source evidence remains visible/advisory and does not
    // change the generic radiator mount search order until a future condition resolver can
    // prove that the current build satisfies it.
    if(Array.isArray(hint?.conditions) && hint.conditions.length)continue;
    const sizes=Array.isArray(hint?.sizes)?hint.sizes.map(Number).filter(Number.isFinite):[];
    if(selectedSize>0 && sizes.length && !sizes.some(size=>Math.abs(size-selectedSize)<=1))continue;
    const maxRadiatorThickness=finite(hint?.maxRadiatorThickness,0);
    const maxStack=finite(hint?.maxStack,0);
    if(maxRadiatorThickness>0 && radiatorThickness>0 && radiatorThickness>maxRadiatorThickness+0.25)continue;
    if(maxStack>0 && stackThickness>0 && stackThickness>maxStack+0.25)continue;
    const zone=String(hint?.zone||'').trim().toLowerCase().replace(/[_-]+/g,' ');
    let mounts=[];
    if(zone.includes('top'))mounts=['top'];
    else if(zone.includes('bottom')||zone.includes('lower'))mounts=['bottom'];
    else if(zone.includes('front'))mounts=['front'];
    else if(zone.includes('rear')||zone.includes('back'))mounts=['rear'];
    else if(zone.includes('left'))mounts=['side_left'];
    else if(zone.includes('right'))mounts=['side_right'];
    else if(zone.includes('side'))mounts=['side_right','side_left'];

    // Parsed notes describe the physical chassis. Vertical-reference solving happens in the
    // canonical horizontal reference frame, so translate physical face names into canonical
    // mount names before candidate generation. Sides are invariant under this transform.
    if(effective?.layoutFamily==='reference' && effective?.layoutTransform==='vertical'){
      const physicalToCanonical={top:'front',bottom:'rear',front:'bottom',rear:'top'};
      mounts=mounts.map(mount=>physicalToCanonical[mount]||mount);
    }
    mounts.forEach(push);
  }
  return out;
}

export function preferredRadiatorMounts(family, effective={}, radiatorSize=0, radiatorSpec={}) {
  const explicit=String(effective?.spatialHints?.radiatorMount || effective?.spatialHints?.aioMount || '').toLowerCase();
  if (explicit) {
    const tokens=explicit.split(/[\s,|/]+/).filter(Boolean);
    const normalized=tokens.map(v=>v==='upper'?'top':v==='lower'?'bottom':v);
    if (normalized.length) return normalized;
  }

  // Deterministically parsed source notes are preference evidence, not a mount whitelist.
  // They may unlock an otherwise-secondary reference mount (e.g. an explicitly documented
  // rear radiator), but the established family fallback order remains available afterwards.
  const defaults=family==='reference'
    ? ['top','side_right','side_left']
    : family==='sandwich'
      ? ['top','side_right','side_left','front','rear','bottom']
      : family==='console'
        ? ['side_right','side_left','front','rear','top','bottom']
        : ['top','side_right','side_left','front','rear','bottom'];
  const source=catalogRadiatorMountPreferences(effective,radiatorSize,radiatorSpec);
  return [...new Set([...source,...defaults])];
}

export function modeUsesVerticalGpu(effective={}) {
  // Explicit vertical-GPU mounting remains available as a component relationship. A
  // vertical-reference chassis no longer enters this path: its ordinary horizontal PCIe
  // relationship is rotated with the complete reference frame by the topology transform.
  return effective?.spatialHints?.gpuOrientation === 'vertical'
    || effective?.spatialHints?.verticalGpu === true
    || effective?.modeTraits?.verticalGpuOnly === true;
}
